import type { IntlShape } from 'react-intl';
import { activityMessages as m } from './activityMessages';

/** "2 min 40 s", "48 s", "3 min": a listen time (design t2). */
export function listenDuration(intl: IntlShape, seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const min = Math.floor(whole / 60);
  const sec = whole % 60;
  if (min === 0) {
    return intl.formatMessage(m.seconds, { sec });
  }
  return sec === 0 ? intl.formatMessage(m.minutes, { min }) : intl.formatMessage(m.minutesSeconds, { min, sec });
}

function dayKey(date: Date, timeZone?: string): string {
  return date.toLocaleDateString('en-CA', timeZone === undefined ? undefined : { timeZone });
}

/**
 * The time column of the timeline (design t2): the clock time today, the short weekday this week,
 * else the short date.
 */
export function activityTime(intl: IntlShape, iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (dayKey(at) === dayKey(now)) {
    return intl.formatTime(at, { hour: '2-digit', minute: '2-digit' });
  }
  if (now.getTime() - at.getTime() < 6 * 24 * 60 * 60 * 1000) {
    return intl.formatDate(at, { weekday: 'short' });
  }
  return intl.formatDate(at, { month: 'short', day: 'numeric' });
}

/** A valid IANA zone, or undefined (an unknown zone must not break the screen). */
export function safeTimeZone(timeZone: string | null): string | undefined {
  if (timeZone === null || timeZone === '') {
    return undefined;
  }
  try {
    Intl.DateTimeFormat('en', { timeZone }).format(0);
    return timeZone;
  } catch {
    return undefined;
  }
}

/** "09:40" in the recipient's time zone (design t2 "their time"), or in the viewer's zone. */
export function clockIn(intl: IntlShape, iso: string, timeZone: string | undefined): string {
  return intl.formatTime(new Date(iso), { hour: '2-digit', minute: '2-digit', timeZone });
}

/** Today, tomorrow or later for the "Up next" rows, as seen in the recipient's zone. */
export function dayRelation(
  iso: string,
  timeZone: string | undefined,
  now: Date = new Date()
): 'today' | 'tomorrow' | 'later' {
  const at = dayKey(new Date(iso), timeZone);
  if (at === dayKey(now, timeZone)) {
    return 'today';
  }
  return at === dayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000), timeZone) ? 'tomorrow' : 'later';
}
